import { can } from "../permissions/policy";
import type { SessionProfile } from "../../shared/types/domain";
import type {
  CreateAccountInput,
  CreateAssignmentInput,
  DisableAccountInput,
  OrganizationState,
  SetMemberCoachesInput,
  UpdateMemberInput,
  UpdateStaffInput,
} from "./organization.types";

const STORAGE_KEY = "boom-v4-organization";
const stores = [
  {
    id: "20000000-0000-4000-8000-000000000001",
    code: "WHTD",
    name: "武汉天地店",
    status: "active" as const,
  },
  {
    id: "20000000-0000-4000-8000-000000000002",
    code: "HYD",
    name: "花园道店",
    status: "active" as const,
  },
  {
    id: "20000000-0000-4000-8000-000000000003",
    code: "QCWXH",
    name: "亲橙万象汇店",
    status: "active" as const,
  },
  {
    id: "20000000-0000-4000-8000-000000000004",
    code: "HFZCSD",
    name: "华发中城商都店",
    status: "active" as const,
  },
];

const seed: OrganizationState = {
  stores,
  accounts: [
    account(
      "10000000-0000-4000-8000-000000000001",
      "18800000001",
      "演示会员",
      "member",
      stores[0].id,
      "会员",
      0,
      0,
    ),
    account(
      "10000000-0000-4000-8000-000000000002",
      "18800000002",
      "演示员工",
      "employee",
      stores[0].id,
      "教练兼前台",
      3,
      2,
    ),
    account(
      "10000000-0000-4000-8000-000000000003",
      "18800000003",
      "演示店长",
      "manager",
      stores[0].id,
      "店长",
      0,
      1,
    ),
    account(
      "10000000-0000-4000-8000-000000000004",
      "18800000004",
      "演示超管",
      "super",
      null,
      "总部管理员",
      0,
      0,
    ),
    account(
      "10000000-0000-4000-8000-000000000005",
      "18800000005",
      "李教练",
      "employee",
      stores[0].id,
      "教练",
      1,
      0,
    ),
    account(
      "10000000-0000-4000-8000-000000000006",
      "18800000006",
      "王教练",
      "employee",
      stores[1].id,
      "教练",
      2,
      1,
    ),
    account(
      "10000000-0000-4000-8000-000000000007",
      "18800000007",
      "花园道店长",
      "manager",
      stores[1].id,
      "店长",
      0,
      0,
    ),
  ],
  assignments: [],
  audit: [],
};

seed.accounts[0].coachId = seed.accounts[1].id;
seed.accounts[0].serviceCoachIds = [seed.accounts[1].id];

function account(
  id: string,
  phone: string,
  name: string,
  role: "member" | "employee" | "manager" | "super",
  storeId: string | null,
  jobTitle: string,
  members: number,
  tasks: number,
): import("./organization.types").AccountRecord {
  return {
    id,
    phone,
    name,
    role,
    storeId,
    jobTitle,
    assignedMemberCount: members,
    openTaskCount: tasks,
    handoverTo: null,
    status: "active" as const,
    createdAt: "2026-07-20T00:00:00.000Z",
    gender: null,
    birthday: null,
    coachId: null,
    serviceCoachIds: [],
    memberStatus: role === "member" ? ("expired" as const) : null,
  };
}

function clone<T>(value: T): T {
  return structuredClone(value);
}

function load(): OrganizationState {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return clone(seed);
  try {
    return JSON.parse(raw) as OrganizationState;
  } catch {
    return clone(seed);
  }
}

function save(state: OrganizationState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}
function fail(message: string): never {
  throw new Error(message);
}

function requireActor(actor: SessionProfile) {
  if (actor.status !== "active") fail("当前账号不是启用状态。");
}

function writeAudit(
  state: OrganizationState,
  actor: SessionProfile,
  action: string,
  entityType: "profile" | "assignment",
  entityId: string,
  reason: string,
) {
  state.audit.unshift({
    id: crypto.randomUUID(),
    actorId: actor.id,
    action,
    entityType,
    entityId,
    reason,
    occurredAt: new Date().toISOString(),
  });
}

export const demoOrganizationRepository = {
  async getState() {
    return load();
  },
  async reset() {
    localStorage.removeItem(STORAGE_KEY);
    return clone(seed);
  },
  async createAccount(actor: SessionProfile, input: CreateAccountInput) {
    requireActor(actor);
    const state = load();
    const phone = input.phone.trim();
    const name = input.name.trim();
    if (!/^1\d{10}$/.test(phone)) fail("手机号必须是11位中国大陆手机号。");
    if (!name) fail("姓名不能为空。");
    if (state.accounts.some((item) => item.phone === phone))
      fail("该手机号已经绑定账号。");
    if (
      !state.stores.some(
        (item) => item.id === input.storeId && item.status === "active",
      )
    )
      fail("所属门店无效或已停用。");
    if (input.role === "member") {
      if (!can(actor.role, "account.member.create"))
        fail("当前角色不能创建会员。");
      if (actor.role !== "super" && actor.storeId !== input.storeId)
        fail("不能跨店创建会员。");
    } else {
      if (!can(actor.role, "account.employee.create"))
        fail("只有超管可以创建员工或店长账号。");
    }
    const next = account(
      crypto.randomUUID(),
      phone,
      name,
      input.role,
      input.storeId,
      input.jobTitle ||
        (input.role === "member"
          ? "会员"
          : input.role === "manager"
            ? "店长"
            : "员工"),
      0,
      0,
    );
    if (input.role === "member") {
      if (!input.gender || !input.birthday || !input.coachId)
        fail("会员性别、生日和主教练必填。");
      const coach = state.accounts.find(
        (item) =>
          item.id === input.coachId &&
          (item.role === "employee" || item.role === "manager") &&
          item.status === "active" &&
          item.storeId === input.storeId,
      );
      if (!coach) fail("主教练必须是本店启用教练或店长。");
      Object.assign(next, {
        gender: input.gender,
        birthday: input.birthday,
        coachId: coach.id,
        serviceCoachIds: [coach.id],
      });
    } else {
      next.birthday = input.birthday || null;
    }
    state.accounts.push(next);
    writeAudit(
      state,
      actor,
      "account.create",
      "profile",
      next.id,
      `创建${input.role}账号`,
    );
    save(state);
    return clone(next);
  },
  async updateStaff(actor: SessionProfile, input: UpdateStaffInput) {
    requireActor(actor);
    if (actor.role !== "super") fail("只有超管可以修改员工或店长资料。");
    const state = load();
    const target =
      state.accounts.find((item) => item.id === input.accountId) ??
      fail("目标账号不存在。");
    if (!(["employee", "manager"] as const).includes(target.role as "employee" | "manager"))
      fail("只能修改员工或店长资料。");
    const phone = input.phone.trim();
    const name = input.name.trim();
    const jobTitle = input.jobTitle.trim();
    if (!/^1\d{10}$/.test(phone)) fail("手机号必须是11位中国大陆手机号。");
    if (!name) fail("姓名不能为空。");
    if (!jobTitle) fail("岗位不能为空。");
    if (input.birthday && input.birthday > new Date().toISOString().slice(0, 10))
      fail("生日不能晚于今天。");
    if (state.accounts.some((item) => item.id !== target.id && item.phone === phone))
      fail("该手机号已经绑定账号。");
    if (!state.stores.some((item) => item.id === input.storeId && item.status === "active"))
      fail("所属门店无效或已停用。");
    if (target.storeId !== input.storeId && target.assignedMemberCount > 0)
      fail("该人员仍有关联会员，请先完成交接后再调整门店。");
    Object.assign(target, {
      phone,
      name,
      role: input.role,
      storeId: input.storeId,
      jobTitle,
      birthday: input.birthday || null,
    });
    writeAudit(state, actor, "account.update", "profile", target.id, "总部修改员工资料");
    save(state);
    return clone(target);
  },
  async updateMember(actor: SessionProfile, input: UpdateMemberInput) {
    requireActor(actor);
    if (!can(actor.role, "member.update")) fail("当前角色不能修改会员资料。");
    const state = load();
    const target =
      state.accounts.find(
        (item) => item.id === input.memberId && item.role === "member",
      ) ?? fail("会员档案不存在。");
    const hasDelegation = state.assignments.some(
      (assignment) =>
        assignment.employeeId === actor.id &&
        assignment.memberId === target.id &&
        assignment.storeId === target.storeId &&
        new Date(assignment.startsAt) <= new Date() &&
        new Date(assignment.endsAt) > new Date(),
    );
    const inScope =
      actor.role === "super" ||
      (actor.role === "manager" && actor.storeId === target.storeId) ||
      (actor.role === "employee" &&
        (target.serviceCoachIds?.includes(actor.id) || target.coachId === actor.id || hasDelegation));
    if (!inScope) fail("只能修改当前可见范围内的会员。");
    const name = input.name.trim();
    if (!name || name.length > 80) fail("会员姓名必须为1至80个字符。");
    if (!( ["male", "female", "undisclosed"] as const).includes(input.gender))
      fail("会员性别无效。");
    if (input.birthday && input.birthday > new Date().toISOString().slice(0, 10))
      fail("生日不能晚于今天。");
    Object.assign(target, {
      name,
      gender: input.gender,
      birthday: input.birthday || null,
    });
    writeAudit(
      state,
      actor,
      "member.profile.update",
      "profile",
      target.id,
      "MEMBER_PROFILE_EDIT",
    );
    save(state);
    return clone(target);
  },
  async setMemberCoaches(actor: SessionProfile, input: SetMemberCoachesInput) {
    requireActor(actor);
    if (actor.role !== "manager" && actor.role !== "super")
      fail("只有店长或超管可以调整会员授课教练。");
    const state = load();
    const target = state.accounts.find(
      (item) => item.id === input.memberId && item.role === "member",
    ) ?? fail("会员档案不存在。");
    if (actor.role === "manager" && actor.storeId !== target.storeId)
      fail("店长只能调整本店会员的授课教练。");
    const selected = Array.from(
      new Set([...input.serviceCoachIds, input.primaryCoachId]),
    );
    if (!input.primaryCoachId || selected.length < 1 || selected.length > 5)
      fail("每名会员必须有1名主教练，授课教练最多5名。");
    const now = new Date();
    selected.forEach((coachId) => {
      const coach = state.accounts.find(
        (item) =>
          item.id === coachId &&
          (item.role === "employee" || item.role === "manager") &&
          item.status === "active",
      );
      const delegated = state.assignments.some(
        (assignment) =>
          assignment.employeeId === coachId &&
          assignment.memberId === target.id &&
          assignment.storeId === target.storeId &&
          new Date(assignment.startsAt) <= now &&
          new Date(assignment.endsAt) > now,
      );
      if (!coach || (coach.storeId !== target.storeId && !delegated))
        fail("授课教练必须属于本店，跨店教练需先完成指定会员授权。");
    });
    target.coachId = input.primaryCoachId;
    target.serviceCoachIds = selected;
    writeAudit(
      state,
      actor,
      "member.coaches.update",
      "profile",
      target.id,
      `主教练1名，协作教练${selected.length - 1}名`,
    );
    save(state);
    return clone(target);
  },
  async disableAccount(actor: SessionProfile, input: DisableAccountInput) {
    requireActor(actor);
    const state = load();
    const target =
      state.accounts.find((item) => item.id === input.accountId) ??
      fail("目标账号不存在。");
    if (!can(actor.role, "account.disable")) fail("当前角色不能停用账号。");
    if (actor.id === target.id) fail("不能停用自己的账号。");
    if (
      actor.role === "manager" &&
      (target.role !== "employee" || target.storeId !== actor.storeId)
    )
      fail("店长只能停用本店普通员工。");
    if (target.role === "super") fail("超管账号不能通过该流程停用。");
    if (!input.reason.trim()) fail("停用原因不能为空。");
    const requiresHandover =
      target.assignedMemberCount > 0 || target.openTaskCount > 0;
    if (requiresHandover && !input.handoverTo)
      fail("该人员仍有会员或任务，必须先选择交接人。");
    if (input.handoverTo) {
      const receiver = state.accounts.find(
        (item) => item.id === input.handoverTo,
      );
      if (
        !receiver ||
        receiver.status !== "active" ||
        receiver.role !== "employee" ||
        receiver.storeId !== target.storeId ||
        receiver.id === target.id
      )
        fail("交接人必须是同店其他启用员工。");
      receiver.assignedMemberCount += target.assignedMemberCount;
      receiver.openTaskCount += target.openTaskCount;
      target.handoverTo = receiver.id;
      target.assignedMemberCount = 0;
      target.openTaskCount = 0;
    }
    target.status = "disabled";
    writeAudit(
      state,
      actor,
      "account.disable",
      "profile",
      target.id,
      input.reason.trim(),
    );
    save(state);
    return clone(target);
  },
  async restoreAccount(
    actor: SessionProfile,
    accountId: string,
    reason: string,
  ) {
    requireActor(actor);
    const state = load();
    const target =
      state.accounts.find((item) => item.id === accountId) ??
      fail("目标账号不存在。");
    if (!can(actor.role, "account.restore")) fail("当前角色不能恢复账号。");
    if (
      actor.role === "manager" &&
      (target.role !== "employee" || target.storeId !== actor.storeId)
    )
      fail("店长只能恢复本店普通员工。");
    if (!reason.trim()) fail("恢复原因不能为空。");
    target.status = "active";
    writeAudit(
      state,
      actor,
      "account.restore",
      "profile",
      target.id,
      reason.trim(),
    );
    save(state);
    return clone(target);
  },
  async createAssignment(actor: SessionProfile, input: CreateAssignmentInput) {
    requireActor(actor);
    if (!can(actor.role, "delegation.manage"))
      fail("只有超管可以创建跨店授权。");
    const state = load();
    const employee =
      state.accounts.find(
        (item) =>
          item.id === input.employeeId &&
          item.role === "employee" &&
          item.status === "active",
      ) ?? fail("请选择启用员工。");
    if (employee.storeId === input.storeId)
      fail("员工主门店与支援门店不能相同。");
    if (
      !state.stores.some(
        (item) => item.id === input.storeId && item.status === "active",
      )
    )
      fail("目标门店无效。");
    const member = state.accounts.find(
      (item) =>
        item.id === input.memberId &&
        item.role === "member" &&
        item.status === "active" &&
        item.storeId === input.storeId,
    );
    if (!member) fail("跨店授权必须绑定目标门店的一名启用会员。");
    if (!input.reason.trim()) fail("授权原因不能为空。");
    const starts = new Date(input.startsAt);
    const ends = new Date(input.endsAt);
    if (
      !Number.isFinite(starts.getTime()) ||
      !Number.isFinite(ends.getTime()) ||
      ends <= starts
    )
      fail("授权结束时间必须晚于开始时间。");
    const now = new Date();
    const next = {
      id: crypto.randomUUID(),
      employeeId: employee.id,
      storeId: input.storeId,
      memberId: member.id,
      startsAt: starts.toISOString(),
      endsAt: ends.toISOString(),
      approvedBy: actor.id,
      reason: input.reason.trim(),
      status: (ends <= now
        ? "expired"
        : starts <= now
          ? "active"
          : "scheduled") as "expired" | "active" | "scheduled",
    };
    state.assignments.push(next);
    writeAudit(
      state,
      actor,
      "delegation.create",
      "assignment",
      next.id,
      next.reason,
    );
    save(state);
    return clone(next);
  },
};
